import { Flex, SimpleGrid, Stack } from '@chakra-ui/react';
import React from 'react';

import { ClaimsSummary } from 'Containers/ProfilePage/types';
import { useTextContext } from 'Contexts/textContext';
import { Button, H5, Icon, Numbers, Paragraph, Spinner } from 'DesignLibrary/atoms';
import { BannerCard } from 'DesignLibrary/molecules/BannerCard';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import Text from 'Shared/Text';
import { toDollars } from 'Utils/helpers';

import { ClaimsCard, ClaimsTitle, ClaimsLoadingContainer, Content, SpinnerContainer } from './styled';

export interface ClaimsBannerProps {
  handleOpen: () => void;
  claimsSummary: ClaimsSummary | null;
  claimsSummaryPolling: boolean;
  claimsError: Error | null;
}

export const ClaimsBanner = ({
  handleOpen,
  claimsSummary,
  claimsSummaryPolling,
  claimsError,
}: ClaimsBannerProps) => {
  const { retrieveContentfulData } = useTextContext();

  const renderClaimsNumbers = () => {
    if (claimsSummaryPolling) {
      return (
        <ClaimsLoadingContainer data-testid="claims-loading-banner">
          <InfoBar
            text={
              <SpinnerContainer>
                <Spinner size="large" color="--primary-blue" />
                {retrieveContentfulData('health_section.secondary_content.claims_banner_loading')}
              </SpinnerContainer>
            }
          />
        </ClaimsLoadingContainer>
      );
    }

    if (claimsError || !claimsSummary) {
      return (
        <InfoBar
          icon="Warning"
          color="yellow"
          text={<Text field="health_section.secondary_content.claims_banner_error" />}
        />
      );
    }

    const { oopTowardDeductibleDollars, oopTowardMaxDollars, maxDollars, deductibleDollars } = claimsSummary;

    return (
      <SimpleGrid columns={[1, 2]} className="claims-numbers">
        <div>
          <Numbers size="medium">
            {toDollars(oopTowardDeductibleDollars, 2)}
            <span> /</span>
          </Numbers>{' '}
          <Numbers size="small">{toDollars(deductibleDollars)}</Numbers>
          {retrieveContentfulData<string>('tool_tips.deductible.title').toLowerCase()}
        </div>
        <div>
          <Numbers size="medium">
            {toDollars(oopTowardMaxDollars, 2)}
            <span> /</span>
          </Numbers>{' '}
          <Numbers size="small">{toDollars(maxDollars)}</Numbers>
          {retrieveContentfulData('health_section.secondary_content.claims_banner_oop_max')}
        </div>
      </SimpleGrid>
    );
  };
  return (
    <BannerCard>
      <ClaimsCard>
        <Stack>
          <ClaimsTitle>
            <Flex align="center">
              <Icon type="Calculator" size="large" color="--primary-blue" weight="fill" />
              <H5 weight="normal" data-testid="claims-banner-header">
                <Text field="health_section.secondary_content.claims_banner_header" />
              </H5>
            </Flex>
          </ClaimsTitle>
        </Stack>
        <Content>
          {renderClaimsNumbers()}
          <hr />
          <Paragraph size="small">
            <Text field="health_section.secondary_content.claims_banner_subtext" />
          </Paragraph>
          <Button size="small" stretch onClick={handleOpen} data-testid="claims-breakdown-button">
            <Text field="health_section.secondary_content.claims_banner_button" />
          </Button>
        </Content>
      </ClaimsCard>
    </BannerCard>
  );
};
